import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import useAuthStore from '../../store/authStore';
import DoctorDropdown from '../../components/DoctorDropdown';

const STATUS_COLORS = {
  Scheduled: { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
  Confirmed: { bg: '#f0fdfa', color: '#0f766e', border: '#99f6e4' },
  'In Progress': { bg: '#fffbeb', color: '#92400e', border: '#fde68a' },
  Completed: { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
  'No Show': { bg: '#fef2f2', color: '#991b1b', border: '#fecaca' },
  Cancelled: { bg: '#f9fafb', color: '#6b7280', border: '#e5e7eb' },
  Rescheduled: { bg: '#fff7ed', color: '#9a3412', border: '#fed7aa' },
};

const DEPARTMENTS = ['General', 'Cardiology', 'Dermatology', 'ENT', 'Gynecology', 'Neurology', 'Ophthalmology', 'Orthopedics', 'Pediatrics', 'Psychiatry', 'Radiology', 'Surgery', 'Urology'];
const DURATIONS = [15, 30, 45, 60];

export default function Appointments() {
  const token = useAuthStore(s => s.token);
  const headers = { Authorization: `Bearer ${token}` };

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('today');
  const [filters, setFilters] = useState({ date: '', doctor_id: '', status: '' });
  const [showModal, setShowModal] = useState(false);
  const [editAppt, setEditAppt] = useState(null);
  const [patientSearch, setPatientSearch] = useState('');
  const [patientResults, setPatientResults] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [cancelModal, setCancelModal] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [form, setForm] = useState({
    doctor_id: '', doctor_name: '', department: 'General',
    appointment_date: '', appointment_time: '', duration_minutes: 15,
    reason: '', notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const today = new Date().toISOString().slice(0, 10);

  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeTab === 'today') params.date = today;
      else if (activeTab === 'upcoming') params.from = today;
      if (filters.doctor_id) params.doctor_id = filters.doctor_id;
      if (filters.status) params.status = filters.status;
      if (filters.date) params.date = filters.date;
      const res = await axios.get('/api/appointments', { headers, params });
      setAppointments(res.data || []);
    } catch {
      setAppointments([]);
    }
    setLoading(false);
  }, [activeTab, filters, today]);

  useEffect(() => { fetchAppointments(); }, [fetchAppointments]);

  const searchPatients = async (q) => {
    if (!q || q.length < 2) { setPatientResults([]); return; }
    try {
      const res = await axios.get(`/api/patients?q=${q}`, { headers });
      setPatientResults(res.data?.patients || []);
    } catch { setPatientResults([]); }
  };

  const openNew = () => {
    setEditAppt(null);
    setSelectedPatient(null);
    setPatientSearch('');
    setPatientResults([]);
    setForm({ doctor_id: '', doctor_name: '', department: 'General', appointment_date: today, appointment_time: '', duration_minutes: 15, reason: '', notes: '' });
    setShowModal(true);
  };

  const openEdit = (appt) => {
    setEditAppt(appt);
    setSelectedPatient({ id: appt.patient_id, name: appt.patient_name, mrn: appt.mrn });
    setPatientSearch(appt.patient_name);
    setForm({
      doctor_id: appt.doctor_id || '',
      doctor_name: appt.doctor_name || '',
      department: appt.department || 'General',
      appointment_date: appt.appointment_date?.slice(0, 10) || '',
      appointment_time: appt.appointment_time?.slice(0, 5) || '',
      duration_minutes: appt.duration_minutes || 15,
      reason: appt.reason || '',
      notes: appt.notes || ''
    });
    setShowModal(true);
  };

  const saveAppointment = async () => {
    if (!selectedPatient || !form.appointment_date || !form.appointment_time) {
      setMsg('Patient, date, and time are required.'); return;
    }
    setSaving(true);
    try {
      const payload = { ...form, patient_id: selectedPatient.id };
      if (editAppt) {
        await axios.put(`/api/appointments/${editAppt.id}`, payload, { headers });
      } else {
        await axios.post('/api/appointments', payload, { headers });
      }
      setShowModal(false);
      fetchAppointments();
      setMsg('');
    } catch (e) {
      setMsg(e.response?.data?.error || 'Failed to save appointment.');
    }
    setSaving(false);
  };

  const cancelAppointment = async () => {
    if (!cancelReason.trim()) return;
    try {
      await axios.delete(`/api/appointments/${cancelModal.id}`, { headers, data: { reason: cancelReason } });
      setCancelModal(null);
      setCancelReason('');
      fetchAppointments();
    } catch {}
  };

  const markNoShow = async (id) => {
    try {
      await axios.put(`/api/appointments/${id}`, { status: 'No Show' }, { headers });
      fetchAppointments();
    } catch {}
  };

  const registerNow = async (appt) => {
    try {
      const res = await axios.post(`/api/appointments/${appt.id}/convert`, {}, { headers });
      window.location.href = `/opd/register?patient_id=${res.data.patient_id}&appointment_id=${appt.id}&doctor_name=${encodeURIComponent(res.data.doctor_name || '')}`;
    } catch {}
  };

  const printSlip = (appt) => {
    const w = window.open('', '_blank', 'width=400,height=300');
    w.document.write(`<html><head><title>Appointment Slip</title><style>body{font-family:Arial;padding:20px}h2{margin:0 0 8px}table{width:100%}td{padding:4px 0}td:first-child{font-weight:bold;width:120px}</style></head><body>
      <h2>Appointment Slip</h2><hr/>
      <table>
        <tr><td>Patient:</td><td>${appt.full_name || appt.patient_name} (${appt.mrn})</td></tr>
        <tr><td>Doctor:</td><td>${appt.doctor_name || '-'}</td></tr>
        <tr><td>Department:</td><td>${appt.department || '-'}</td></tr>
        <tr><td>Date:</td><td>${appt.appointment_date?.slice(0, 10)}</td></tr>
        <tr><td>Time:</td><td>${appt.appointment_time?.slice(0, 5)}</td></tr>
        <tr><td>Reason:</td><td>${appt.reason || '-'}</td></tr>
      </table>
    </body></html>`);
    w.print();
  };

  const filteredByStatus = activeTab === 'history'
    ? appointments.filter(a => ['Completed', 'No Show', 'Cancelled'].includes(a.status))
    : activeTab === 'all' ? appointments
    : appointments;

  return (
    <div style={{ padding: '24px', backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', margin: 0 }}>Appointments</h1>
          <p style={{ fontSize: 14, color: '#6b7280', margin: '4px 0 0' }}>Manage and track OPD appointments</p>
        </div>
        <button onClick={openNew} style={{ backgroundColor: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          + Book Appointment
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '2px solid #e5e7eb' }}>
        {[['today', 'Today'], ['upcoming', 'Upcoming'], ['all', 'All'], ['history', 'History']].map(([key, label]) => (
          <button key={key} onClick={() => setActiveTab(key)} style={{
            padding: '10px 20px', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500,
            borderBottom: activeTab === key ? '2px solid #2563eb' : '2px solid transparent',
            backgroundColor: 'transparent', color: activeTab === key ? '#2563eb' : '#6b7280',
            marginBottom: -2
          }}>{label}</button>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <input type="date" value={filters.date} onChange={e => setFilters(f => ({ ...f, date: e.target.value }))}
          style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
        <div style={{ minWidth: 200 }}>
          <DoctorDropdown value={filters.doctor_id} onChange={(id, name) => setFilters(f => ({ ...f, doctor_id: id }))} label="" placeholder="Filter by doctor" />
        </div>
        <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
          style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}>
          <option value="">All Statuses</option>
          {Object.keys(STATUS_COLORS).map(s => <option key={s}>{s}</option>)}
        </select>
        <button onClick={() => setFilters({ date: '', doctor_id: '', status: '' })}
          style={{ padding: '8px 14px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, backgroundColor: '#fff', cursor: 'pointer' }}>
          Clear
        </button>
      </div>

      {/* Table */}
      <div style={{ backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Loading...</div>
        ) : filteredByStatus.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>No appointments found.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: '#f9fafb' }}>
              <tr>
                {['Date', 'Time', 'Patient', 'Doctor', 'Department', 'Reason', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredByStatus.map(appt => {
                const sc = STATUS_COLORS[appt.status] || STATUS_COLORS['Scheduled'];
                return (
                  <tr key={appt.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '12px 16px', fontSize: 13 }}>{appt.appointment_date?.slice(0, 10)}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13 }}>{appt.appointment_time?.slice(0, 5)}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#111' }}>{appt.full_name || appt.patient_name}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>{appt.mrn}</div>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13 }}>{appt.doctor_name || '-'}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13 }}>{appt.department || '-'}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{appt.reason || '-'}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, backgroundColor: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>
                        {appt.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {['Scheduled', 'Confirmed'].includes(appt.status) && (
                          <>
                            <button onClick={() => openEdit(appt)} style={{ fontSize: 11, padding: '3px 8px', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', backgroundColor: '#fff' }}>Edit</button>
                            <button onClick={() => registerNow(appt)} style={{ fontSize: 11, padding: '3px 8px', border: 'none', borderRadius: 4, cursor: 'pointer', backgroundColor: '#2563eb', color: '#fff' }}>Register</button>
                            <button onClick={() => markNoShow(appt.id)} style={{ fontSize: 11, padding: '3px 8px', border: '1px solid #fca5a5', borderRadius: 4, cursor: 'pointer', backgroundColor: '#fef2f2', color: '#991b1b' }}>No Show</button>
                            <button onClick={() => { setCancelModal(appt); setCancelReason(''); }} style={{ fontSize: 11, padding: '3px 8px', border: '1px solid #e5e7eb', borderRadius: 4, cursor: 'pointer', backgroundColor: '#f9fafb', color: '#6b7280' }}>Cancel</button>
                          </>
                        )}
                        <button onClick={() => printSlip(appt)} style={{ fontSize: 11, padding: '3px 8px', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', backgroundColor: '#fff' }}>Print</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Book/Edit Modal */}
      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: 32, width: 560, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{editAppt ? 'Edit Appointment' : 'Book Appointment'}</h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>

            {msg && <div style={{ padding: '10px 14px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 13, color: '#991b1b', marginBottom: 16 }}>{msg}</div>}

            {/* Patient search */}
            <div style={{ marginBottom: 16, position: 'relative' }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Patient *</label>
              <input type="text" value={patientSearch}
                onChange={e => { setPatientSearch(e.target.value); searchPatients(e.target.value); setSelectedPatient(null); }}
                placeholder="Search by name, MRN, or phone..."
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
              {patientResults.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.1)', zIndex: 10, maxHeight: 200, overflowY: 'auto' }}>
                  {patientResults.map(p => (
                    <div key={p.id} onClick={() => { setSelectedPatient(p); setPatientSearch(`${p.full_name} (${p.mrn})`); setPatientResults([]); }}
                      style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f0f9ff'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{p.full_name}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>MRN: {p.mrn} | {p.gender} | {p.age_years}y</div>
                    </div>
                  ))}
                </div>
              )}
              {selectedPatient && <div style={{ marginTop: 4, fontSize: 12, color: '#059669' }}>Selected: {selectedPatient.full_name} ({selectedPatient.mrn})</div>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Doctor</label>
                <DoctorDropdown value={form.doctor_name} onChange={(val) => setForm(f => ({ ...f, doctor_id: null, doctor_name: val }))} label="" />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Department</label>
                <select value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}>
                  {DEPARTMENTS.map(d => <option key={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Date *</label>
                <input type="date" value={form.appointment_date} min={today}
                  onChange={e => setForm(f => ({ ...f, appointment_date: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Time *</label>
                <input type="time" value={form.appointment_time}
                  onChange={e => setForm(f => ({ ...f, appointment_time: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Duration</label>
                <select value={form.duration_minutes} onChange={e => setForm(f => ({ ...f, duration_minutes: parseInt(e.target.value) }))}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}>
                  {DURATIONS.map(d => <option key={d} value={d}>{d} min</option>)}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Reason for Visit</label>
              <input type="text" value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="Chief complaint or reason..."
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Notes</label>
              <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Additional notes..." rows={2}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowModal(false)} style={{ padding: '10px 20px', border: '1px solid #d1d5db', borderRadius: 8, backgroundColor: '#fff', cursor: 'pointer', fontSize: 14 }}>Cancel</button>
              <button onClick={saveAppointment} disabled={saving}
                style={{ padding: '10px 24px', border: 'none', borderRadius: 8, backgroundColor: saving ? '#93c5fd' : '#2563eb', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600 }}>
                {saving ? 'Saving...' : editAppt ? 'Update Appointment' : 'Book Appointment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {cancelModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: 28, width: 420 }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 700 }}>Cancel Appointment</h3>
            <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 16px' }}>Cancelling appointment for <strong>{cancelModal.patient_name}</strong> on {cancelModal.appointment_date?.slice(0, 10)}.</p>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Cancellation Reason *</label>
              <textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)} rows={3}
                placeholder="Enter reason for cancellation..."
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setCancelModal(null)} style={{ padding: '8px 18px', border: '1px solid #d1d5db', borderRadius: 8, backgroundColor: '#fff', cursor: 'pointer', fontSize: 14 }}>Back</button>
              <button onClick={cancelAppointment} disabled={!cancelReason.trim()}
                style={{ padding: '8px 18px', border: 'none', borderRadius: 8, backgroundColor: cancelReason.trim() ? '#ef4444' : '#fca5a5', color: '#fff', cursor: cancelReason.trim() ? 'pointer' : 'not-allowed', fontSize: 14, fontWeight: 600 }}>
                Confirm Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
